import { lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { useScrollAnimation } from "./useScrollAnimation";
import { ArrowRight } from "lucide-react";
import contactBannerAsset from "@/assets/contact-banner.png.asset.json";
const contactBanner = contactBannerAsset.url;
const ScienceAnimationLight = lazy(() => import("./ScienceAnimationLight"));
import { useLanguage } from "@/hooks/useLanguage";
import { localizeHref } from "@/lib/localizeHref";
import { getHomepageT } from "@/i18n/homepage";

interface ContactCTAProps {
  onContactClick?: () => void;
  title?: string;
  description?: string;
  partnerLogo?: { src: string; alt: string };
  ctaLabel?: string;
}

const ContactCTA = ({ onContactClick, title, description, partnerLogo, ctaLabel }: ContactCTAProps = {}) => {
  const { ref, isInView } = useScrollAnimation();
  const lang = useLanguage();
  const t = getHomepageT("contactCTA", lang);
  

  const handleClick = (e: React.MouseEvent) => {
    if (onContactClick) {
      e.preventDefault();
      onContactClick();
    }
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 sm:py-32 overflow-hidden"
      style={{ contentVisibility: "auto", containIntrinsicSize: "auto 500px" }}
    >
      <img src={contactBanner} alt="" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
      <div className="absolute inset-0 bg-primary/80" />
      {isInView && (
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none">
          <Suspense fallback={null}><ScienceAnimationLight /></Suspense>
        </div>
      )}
      <div className="absolute top-1/4 -left-20 w-72 h-72 rounded-full bg-accent/15 blur-[100px] pointer-events-none" />
      <div className="absolute bottom-1/4 -right-16 w-56 h-56 rounded-full bg-accent/10 blur-[80px] pointer-events-none" />

      <div className="container mx-auto px-6 lg:px-10 relative z-10">
        <motion.div
          className="text-center max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 25 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground tracking-tight leading-tight mb-5">
            {title || t.heading}
          </h2>
          <p className="text-primary-foreground/70 text-base mb-10">
            {description || t.body}
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={onContactClick ? "#" : localizeHref("/en/contact", lang)}
              onClick={handleClick}
              className="group inline-flex items-center gap-2 bg-primary-foreground text-primary px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-foreground/90 hover:shadow-xl hover:scale-[1.03] transition-all duration-300"
            >
              {ctaLabel || t.cta} <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
            </a>
          </div>
          {partnerLogo && (
            <motion.div
              className="mt-10"
              initial={{ opacity: 0, y: 10 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <img src={partnerLogo.src} alt={partnerLogo.alt} className="h-12 sm:h-16 w-auto object-contain mx-auto brightness-0 invert opacity-80" />
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default ContactCTA;
